import React, { useState, useMemo } from 'react';
import { ExternalLink, Calendar, Tag } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { projects } from '../mock';

const Projects = () => {
  const [filter, setFilter] = useState('All');

  const allTags = useMemo(() => {
    const tags = projects.reduce((acc, project) => {
      project.tags.forEach(tag => acc.add(tag));
      return acc;
    }, new Set());
    return ['All', ...Array.from(tags)];
  }, []);
  
  const filteredProjects = useMemo(() => {
    if (filter === 'All') return projects;
    return projects.filter(p => p.tags.includes(filter));
  }, [filter]);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-background via-background to-cyan-950/20 dark:to-cyan-950/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            My <span className="text-cyan-400">Projects</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            A showcase of successful implementations, migrations, and innovations that have transformed operations and driven measurable results.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-accent/30 sticky top-16 z-40 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {allTags.map((tag) => (
              <Button
                key={tag}
                variant={filter === tag ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(tag)}
                className={filter === tag 
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                  : 'border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10'
                }
              >
                {tag}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">No projects found with this filter.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {filteredProjects.map((project) => (
                <Card
                  key={project.id}
                  className="group border-border hover:border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/10 overflow-hidden"
                >
                  {/* Project Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
                    
                    {/* Date Badge */}
                    <div className="absolute top-4 right-4 flex items-center gap-2 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full">
                      <Calendar className="h-3 w-3 text-cyan-400" />
                      <span className="text-sm text-muted-foreground">{project.date}</span>
                    </div>
                  </div>

                  <CardContent className="p-6">
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tags.map((tag, idx) => (
                        <Badge
                          key={idx}
                          variant="secondary"
                          className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20"
                        >
                          <Tag className="h-3 w-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    {/* Title & Description */}
                    <h3 className="text-2xl font-bold mb-3 group-hover:text-cyan-400 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {project.description}
                    </p>

                    {/* Achievements */}
                    <div className="space-y-2 mb-4">
                      <h4 className="text-sm font-semibold text-cyan-400">Key Achievements:</h4>
                      <ul className="space-y-1">
                        {project.achievements.map((achievement, idx) => (
                          <li key={idx} className="text-sm text-muted-foreground flex items-start">
                            <span className="text-cyan-400 mr-2">•</span>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* View Details Button */}
                    <Button
                      variant="outline"
                      className="w-full border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500 group/btn"
                      onClick={() => alert('Project details modal will be implemented with backend!')}
                    >
                      View Details
                      <ExternalLink className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Projects;